import React, { useCallback, useState } from 'react'
import styles from './styles.module.css'
import { Modal } from 'shared'
import { connect } from 'formik'
import { formatCents, parseCents, sanitizeMoney } from '@betterplace/utils'

import classNames from 'classnames'
import { Config, Display, FormValues } from 'donate/types'
import { FormikContextType } from 'formik'
import { MaxCalculator } from 'donate/platform/max_calculator'
import { ModalContentProps } from '../../shared/codonation_selection/types'
import { gaEvent } from 'shared'

export const CodonationSelection = ({
  config,
  display,
  formik,
}: {
  config: Config
  display: Display
  formik: FormikContextType<FormValues>
}): JSX.Element => {
  const [showCodonationModal, setShowCodonationModal] = useState(false)
  const openModal = useCallback(() => {
    gaEvent('engagement', 'click codonation_modal_open_link')
    setShowCodonationModal(true)
    const ele = document.activeElement as HTMLElement | null
    ele?.blur()
  }, [])
  const hideCodonationModal = useCallback(() => {
    setShowCodonationModal(false)
  }, [])

  const hideCodonationModalAndSendEvent = useCallback(() => {
    gaEvent('engagement', 'click codonation_modal_close_link')
    hideCodonationModal()
  }, [hideCodonationModal])

  return (
    <div className="donations-form-codonation-selection" style={{ textAlign: 'left' }}>
      <button type="button" className={styles.modalTrigger} onClick={openModal}>
        <div className={styles.fakeInput}>
          <div>{formatCents(i18n.locale, formik.values.codonation_cents, { format: '%v' })}</div>
          <div>€</div>
        </div>
        <div className="btn btn-large btn-outline-green">{i18n.t('donate.codonation_box.trigger_button')}</div>
      </button>

      <Modal
        id="codonation-selection-modal"
        onHide={hideCodonationModalAndSendEvent}
        show={showCodonationModal}
        header={i18n.t('donate.codonation_box.modal_headline')}
        size="modal-lg"
      >
        <ModalContent config={config} display={display} handleClose={hideCodonationModal} />
      </Modal>
    </div>
  )
}

const ModalContent = connect<ModalContentProps, FormValues>(({ formik, handleClose }) => {
  const maxAmount = MaxCalculator.max(formik.values.amount_cents) / 100

  const [value, setValue] = useState(formik.values.codonation_cents / 100)
  const [valueText, setValueText] = useState(formatCents(i18n.locale, formik.values.codonation_cents, { format: '%v' }))

  const storeAndClose = useCallback(
    (cents) => {
      void formik.setFieldValue('codonation_cents', cents)
      formik.setFieldTouched('codonation_cents', true)
      handleClose()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleClose, formik.setFieldValue, formik.setFieldTouched]
  )

  const handleApplyButtonClick = useCallback(() => {
    storeAndClose(Math.round(value * 100))
    gaEvent('engagement', 'change codonation_amount_input', sanitizeMoney(value.toString()))
  }, [storeAndClose, value])

  const handleEnter: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key == 'Enter') {
        storeAndClose(parseCents((event.target as HTMLInputElement).value))
      }
    },
    [storeAndClose]
  )
  const onValueChanged: React.ChangeEventHandler<HTMLInputElement> = useCallback(({ target }) => {
    const val = sanitizeMoney(target.value.toString())
    setValue(parseCents(val) / 100)
    setValueText(val)
  }, [])

  const CodonationFlowers = ({ value, maxAmount }: { value: number; maxAmount: number }) => {
    const currentPercentage = (value / maxAmount) * 100

    const images: {
      showImage: boolean
      src: string
    }[] = [
      {
        showImage: 0 <= currentPercentage && currentPercentage < 3,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-0-e39b5c5747e825e6b78fae47f6f991347a2e6465cb9aedcdba89296f258b2c77.png',
      },
      {
        showImage: 3 <= currentPercentage && currentPercentage < 8,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-1-aca140a3b2a74ad65be7ce93422afc37e6ca001ce93c44ee97aea72c8e9509d5.png',
      },
      {
        showImage: 8 <= currentPercentage && currentPercentage < 14,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-2-e91118ed0da282016c6dfafbfbb205a1b543f21a658188b277bd1431fed08933.png',
      },
      {
        showImage: 14 <= currentPercentage && currentPercentage < 22,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-3-1e395bb471ac74d84b464698d679d7f786c51da262e3cb5ffb7cc2ada7c45193.png',
      },
      {
        showImage: 22 <= currentPercentage && currentPercentage < 34,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-4-22c82f7f9f6efc1fe94657b80103052c7428e34652e1985a41bbcb23db5ffe46.png',
      },
      {
        showImage: 34 <= currentPercentage && currentPercentage < 48,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-5-1ded43de393f6436c062886bf2ee7ae4e5b419286adb4de4d6157ff83aefa453.png',
      },
      {
        showImage: 48 <= currentPercentage && currentPercentage < 66,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-6-6ec510b3fa5a8afe50817f351c9b5f1db73079922dab0a7176b50e13f02bf3ff.png',
      },
      {
        showImage: 66 <= currentPercentage && currentPercentage < 88,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-7-7d86dc933b3fbfdbe829c3c1ea92033d201578227d412c9893f409755e6a5605.png',
      },
      {
        showImage: 88 <= currentPercentage,
        src: 'https://storage.googleapis.com/betterplace-compiled-assets/assets/donations/codonation/slider/level-8-b2971854e78cf9c0f1c31bca71813e81447a7b5ec3639e36b8356c1ec5787ee2.png',
      },
    ]

    return (
      <>
        {images.map((image, index) => (
          <img
            className={classNames(styles.image, image.showImage && styles.visibleImage)}
            src={image.src}
            key={index}
            alt=""
          />
        ))}
      </>
    )
  }

  return (
    <div>
      <p>{i18n.t('donate.codonation_box.modal_text')}</p>

      <div className={styles.codonationFormWrapper}>
        <div className={styles.illustration}>
          <CodonationFlowers value={value} maxAmount={maxAmount} />
        </div>

        <div className={styles.slider}>
          <input onChange={onValueChanged} value={value} max={maxAmount} min={0} type="range" />
        </div>

        <div className={styles.inputAndSum}>
          <div className={styles.inputFlexContainer}>
            <div className={styles.inputWrapper}>
              <input
                className={styles.input}
                data-ci-id="codonation-input"
                type="text"
                value={valueText}
                onChange={onValueChanged}
                onKeyPress={handleEnter}
              />
              <span className={styles.inputEuroSign}>€</span>
            </div>
          </div>

          <div>&nbsp;=&nbsp;</div>

          <div className={styles.sum}>
            {i18n.t('donate.codonation_box.sum_label')}&nbsp;
            {formatCents(i18n.locale, value * 100 + formik.values.amount_cents, { precision: 2 })}
          </div>
        </div>

        <button
          className="btn btn-primary btn-large mt-4"
          data-dismiss="modal"
          type="button"
          onClick={handleApplyButtonClick}
        >
          {i18n.t('donate.codonation_box.apply_button')}
        </button>
      </div>
    </div>
  )
})
